import React from 'react';
import { AlertCircle, Loader } from 'lucide-react';
import { useDocumentUrl } from '@api/hooks/useDocumentUrl';

interface DocumentViewerProps {
  blobUri?: string;
  className?: string;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ blobUri, className = '' }) => {
  const { data: url, isLoading, error } = useDocumentUrl(blobUri);
  if (!blobUri) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg ${className}`}>
        <p className="text-gray-500 dark:text-gray-400 text-sm">No document available</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg ${className}`}>
        <div className="flex flex-col items-center gap-2">
          <Loader className="h-5 w-5 animate-spin text-gray-500 dark:text-gray-400" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-red-50 dark:bg-red-950 rounded-lg p-4 ${className}`}>
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              Failed to load document
            </p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg ${className}`}>
        <p className="text-gray-500 dark:text-gray-400 text-sm">Document URL not available</p>
      </div>
    );
  }

  // Check if it's a PDF or image
  const isPdf = blobUri.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp|tiff)$/i.test(blobUri.toLowerCase());

  if (isPdf) {
    return (
      <iframe
        src={url}
        className={`w-full rounded-lg border border-gray-200 dark:border-gray-700 ${className}`}
        style={{ minHeight: '500px' }}
        title="Document"
      />
    );
  }

  if (isImage) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden ${className}`}>
        <img
          src={url}
          alt="Document"
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );
  }

  // Fallback for unknown file types - try to display as image first
  // return (
  //   <div className={`flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden ${className}`}>
  //     <img
  //       src={url}
  //       alt="Document"
  //       onError={(e) => {
  //         const target = e.target as HTMLImageElement;
  //         target.style.display = 'none';
  //         target.parentElement!.innerHTML = '<p class="text-gray-500 dark:text-gray-400 text-sm">Document type not supported for preview</p>';
  //       }}
  //       className="max-w-full max-h-full object-contain"
  //     />
  //   </div>
  // );
};

export default DocumentViewer;
